import {
  stripWorksReceivables,
  stripWorksExport,
  WORKS_REPORT_COLLECTIONS,
} from "@utils/worksReceivables";
import Cookies from "universal-cookie";
import {
  useDashboardUIActions,
} from "@contexts/dashboardUI";
import { withApiAuthorization } from "@utils/authenticatedFetch";
import { isPresentationProject, presentationReadOnlyResult } from "@utils/presentationMode";
import buildProjectionScopeKey, {
  buildProjectionRequestKey,
  nextProjectionRequestId,
} from "./projectionScope";
import { loadUpdateJob, requestUpdateJob } from "./updateJobs";

const cookies = new Cookies();

let projectRuntimeIdentity = null;

export const setProjectRuntimeIdentity = (identity) => {
  const id = identity?.id ?? identity?._id;
  const token = identity?.token;
  projectRuntimeIdentity = (
    typeof id === "string" && id.length > 0
    && typeof token === "string" && token.length > 0
  ) ? { id, token } : null;
};

const getProjectIdentity = () => projectRuntimeIdentity || cookies.get("userId") || {};

export const withProjectAuthorization = withApiAuthorization;

const apiFetch = (url, options = {}) => {
  const { token } = getProjectIdentity();
  return globalThis.fetch(url, withProjectAuthorization({
    url,
    options,
    serverUrl: process.env.SERVER_URL,
    token,
  }));
};

export const normalizeCurveResponse = (response, page, pageSize) => ({
  ...response,
  items: Array.isArray(response?.items) ? response.items : [],
  page: Number(response?.page) || page,
  pageSize: Number(response?.pageSize) || pageSize,
  totalItems: Number(response?.totalItems) || 0,
  totalPages: Number(response?.totalPages) || 0,
  totalValue: response?.totalValue ?? null,
});

const scheduleDefaults = {
  page: 1,
  limit: 20,
  status: "needs_attention",
  groupBy: "activity",
  search: "",
};

const scheduleDateKey = (value) => (
  value instanceof Date && !Number.isNaN(value.getTime())
    ? value.toISOString()
    : ""
);

export const buildScheduleRequestKey = (options = {}) => {
  const normalized = { ...scheduleDefaults, ...options };
  return [
    normalized.wbs ?? "",
    normalized.page,
    normalized.limit,
    normalized.status,
    normalized.groupBy,
    normalized.search,
    scheduleDateKey(normalized.beginAt),
    scheduleDateKey(normalized.endAt),
  ].join("|");
};

export const createScheduleAnalysisFetcher = ({
  fetchFn = fetch,
  getToken,
  serverUrl,
  dispatch,
}) => async (options = {}) => {
  const normalized = { ...scheduleDefaults, ...options };
  const requestKey = buildScheduleRequestKey(normalized);
  const graphRequestKey = `schedule:${nextProjectionRequestId()}`;
  dispatch({ type: "SCHEDULE_ANALYSIS_START", requestKey });
  dispatch({ type: "GRAPH_DATA_START", requestKey: graphRequestKey });
  const params = new URLSearchParams({
    wbs: normalized.wbs ?? "",
    include_schedule_analysis: "true",
    schedule_page: String(normalized.page),
    schedule_limit: String(normalized.limit),
    schedule_status: normalized.status,
    schedule_group_by: normalized.groupBy,
    schedule_search: normalized.search,
  });
  const beginAt = scheduleDateKey(normalized.beginAt);
  const endAt = scheduleDateKey(normalized.endAt);
  if (beginAt) params.set("begin_at", beginAt);
  if (endAt) params.set("end_at", endAt);
  try {
    const response = await fetchFn(
      `${serverUrl}/project/getGraphData?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${getToken?.() ?? ""}`,
        },
      },
    );
    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    if (!response.ok || !body?.scheduleAnalysis) {
      throw new Error(
        body?.message || "Não foi possível carregar a análise do cronograma.",
      );
    }
    if (["predicted", "measured", "baseline", "scheduleSource"].some(
      (field) => Object.hasOwn(body, field),
    )) {
      const {
        paidValues,
        disbursements,
        toPayValues,
        physicals,
        predicted,
        baseline,
        measured,
        scheduleCurves,
        scheduleSource,
        takenTo,
        takenFrom,
      } = body;
      dispatch({
        type: "UPDATE_GRAPH_DATA",
        requestKey: graphRequestKey,
        data: {
          bgn: normalized.beginAt,
          nd: normalized.endAt,
          paidValues,
          disbursements,
          toPayValues,
          physicals,
          predicted,
          baseline,
          measured,
          scheduleCurves,
          scheduleSource,
          takenTo,
          takenFrom,
        },
      });
    }
    dispatch({
      type: "SCHEDULE_ANALYSIS_SUCCESS",
      requestKey,
      data: body.scheduleAnalysis,
    });
    return body.scheduleAnalysis;
  } catch (error) {
    dispatch({
      type: "SCHEDULE_ANALYSIS_FAILURE",
      requestKey,
      error: error?.message || "Não foi possível carregar a análise do cronograma.",
    });
    return null;
  }
};


export default (state, dispatch, runtime = {}) => {
  const fetch = apiFetch;
  const projectionInFlight = runtime.projectionInFlight ?? new Map();
  runtime.projectionInFlight = projectionInFlight;
  const presentationMutationRejection = () => (
    isPresentationProject(state.activeProject) ? presentationReadOnlyResult() : null
  );
  const getScheduleAnalysis = createScheduleAnalysisFetcher({
    fetchFn: (...args) => fetch(...args),
    getToken: () => getProjectIdentity().token,
    serverUrl: process.env.SERVER_URL,
    dispatch,
  });
  const list = async (activeId, identity, { selectActive = true } = {}) => {
    if (identity) setProjectRuntimeIdentity(identity);
    const { id } = getProjectIdentity();
    if (!id) {
      dispatch({
        type: "FOUND_PROJECTS",
        data: { projects: [], companies: [] }
      });
      return;
    }
    const response = await fetch(`${process.env.SERVER_URL}/project?user_id=${id}`)
    if (response.ok) {
      const { projects, companies } = await response.json();
      if (selectActive && projects.length > 0) {
        if (projects.find(({ _id }) => _id === activeId)) {
          await selectProject(activeId);
        } else {
          await selectProject(projects[0]._id)
        }
      }
      dispatch({
        type: "FOUND_PROJECTS",
        data: { projects, companies }
      })
    } else {
      dispatch({
        type: "FOUND_PROJECTS",
        data: []
      })
    }
  }

  const selectProject = async (_id) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const { filterDates: { beginAt, endAt } } = state
    const response = await fetch(`${process.env.SERVER_URL}/project/${_id}/detail?begin_at=${beginAt.toISOString()}&end_at=${endAt.toISOString()}`)
    if (response.ok) {
      const { project } = await response.json();
      dispatch({
        type: "SELECT_PROJECT",
        data: stripWorksReceivables(project),
      })
    }
    setIsLoading(false)
    return response.ok
  }

  const updateProjectLive = (project) => {
    dispatch({
      type: "SELECT_PROJECT",
      data: stripWorksReceivables(project),
    })
  }

  const updateProjectionTableView = (state) => {
    dispatch({
      type: "UPDATE_PROJECTION_TABLE_VIEW",
      data: state
    })
  }

  const updateProjectionTableDate = (state) => {
    dispatch({
      type: "UPDATE_PROJECTION_TABLE_DATE",
      data: state
    })
  }

  const changeDates = async (_id, begin, end) => {
    dispatch({
      type: "CHANGE_FILTER_DATES",
      data: {
        begin, end
      }
    })
    getFollowUps(_id, begin, end)
  }

  const updateCurveID = async (id) => {
    dispatch({
      type: 'UPDATE_CURVE_ID',
      data: id
    })
  }

  const updateCubBased = async (state) => {
    dispatch({
      type: 'UPDATE_CUB_BASED',
      data: state
    })
  }

  const updateForecastBased = async (state) => {
    dispatch({
      type: 'UPDATE_FORECAST_BASED',
      data: state
    })
  }


  const getFollowUps = async (_id, begin, end) => {
    const { setIsLoading } = useDashboardUIActions();

    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/${_id}/detail/followups?begin_at=${begin.toISOString()}&end_at=${end.toISOString()}`)
    if (response.ok) {
      const result = stripWorksReceivables(await response.json());
      const { followUps, paidValues, disbursements, toPayValues, takenTo, takenFrom, accumulatedDisbursement, accumulatedPaid, physicals, ampliDisbursements, cashProjection, remaining, access, scheduleSource, metrics } = result;
      dispatch({
        type: "UPDATE_FOLLOW_UPS",
        data: {
          begin,
          end,
          followUps,
          paidValues,
          disbursements,
          toPayValues,
          takenFrom,
          takenTo,
          accumulatedDisbursement,
          accumulatedPaid,
          physicals,
          ampliDisbursements,
          cashProjection,
          remaining,
          access,
          scheduleSource,
          metrics,

        }
      })
    }
    setIsLoading(false)
  }

  const updateMaxDates = async (begin, end) => {
    dispatch({
      type: "UPDATE_GRAPH_MAX_DATES",
      data: {
        begin, end
      }
    })
  }

  const changeGraphDates = async (
    wbs,
    begin,
    end,
    { refreshGraph = true } = {},
  ) => {

    dispatch({
      type: "CHANGE_GRAPH_FILTER_DATES",
      data: {
        begin, end
      }
    })

    if (refreshGraph) return getSingleFollowUps(wbs, begin, end)
    return undefined
  }

  const getSingleFollowUps = async (wbs, begin, end) => {
    const { setIsLoading } = useDashboardUIActions();
    let bgn, nd

    if (!begin || !end) {
      bgn = state.offCanvasFilterDates.beginAt
      nd = state.offCanvasFilterDates.endAt
    } else {
      bgn = begin
      nd = end
    }

    const graphRequestKey = `graph:${nextProjectionRequestId()}`;
    dispatch({ type: "GRAPH_DATA_START", requestKey: graphRequestKey });
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/getGraphData?begin_at=${bgn.toISOString()}&end_at=${nd.toISOString()}&wbs=${wbs}`)
    if (response.ok) {
      const {
        paidValues,
        disbursements,
        toPayValues,
        physicals,
        predicted,
        baseline,
        measured,
        scheduleCurves,
        scheduleSource,
        takenTo,
        takenFrom,
      } = await response.json();
      dispatch({
        type: "UPDATE_GRAPH_DATA",
        requestKey: graphRequestKey,
        data: {
          bgn,
          nd,
          paidValues,
          disbursements,
          toPayValues,
          physicals,
          predicted,
          baseline,
          measured,
          scheduleCurves,
          scheduleSource,
          takenTo,
          takenFrom
        }
      })
    }
    setIsLoading(false)
  }

  const loadProjectionBreakdown = async ({
    level,
    parent,
    dateString,
    responseField,
    loadedType,
  }) => {
    let beginAt;
    let endAt;
    if (dateString) {
      const [month, year] = dateString.split("/")
      const date = new Date(year, month - 1);
      beginAt = date;
      endAt = date;
    } else {
      beginAt = state.filterDates.beginAt
      endAt = state.filterDates.endAt
    }
    const generation = state.projectionScopeGeneration;
    const workId = state.activeProject._id;
    const scopeKey = buildProjectionScopeKey(workId, beginAt, endAt);
    const requestKey = buildProjectionRequestKey(level, parent);
    const inFlightKey = JSON.stringify([generation, scopeKey, requestKey]);
    const existingRequest = projectionInFlight.get(inFlightKey);
    if (existingRequest) return existingRequest;

    const pendingRequest = (async () => {
      const requestId = nextProjectionRequestId();
      const identity = { generation, workId, scopeKey, requestKey, requestId };
      dispatch({ type: "PROJECTION_SCOPE_STARTED", ...identity });
      dispatch({ type: "PROJECTION_LOAD_STARTED", ...identity });
      const route = parent ? `${parent}/${level}` : level;
      try {
        const response = await fetch(`${process.env.SERVER_URL}/project/${workId}/${route}?begin_at=${beginAt.toISOString()}&end_at=${endAt.toISOString()}`)
        if (!response.ok) {
          dispatch({ type: "PROJECTION_LOAD_FAILED", ...identity });
          return undefined;
        }
        const body = await response.json();
        const items = body?.[responseField];
        if (!Array.isArray(items)) {
          dispatch({ type: "PROJECTION_LOAD_FAILED", ...identity });
          return undefined;
        }
        dispatch({
          type: loadedType,
          ...identity,
          data: {
            [responseField]: items,
            ...(parent ? { wbs_code: parent } : {}),
          },
        })
        return items;
      } catch {
        dispatch({ type: "PROJECTION_LOAD_FAILED", ...identity });
        return undefined;
      }
    })();
    projectionInFlight.set(inFlightKey, pendingRequest);
    try {
      return await pendingRequest;
    } finally {
      if (projectionInFlight.get(inFlightKey) === pendingRequest) {
        projectionInFlight.delete(inFlightKey);
      }
    }
  }

  const loadUnits = (dateString) => loadProjectionBreakdown({
    level: "units", dateString, responseField: "units", loadedType: "LOADED_UNITS",
  });

  const loadCells = (parent, dateString) => loadProjectionBreakdown({
    level: "cells", parent, dateString, responseField: "cells", loadedType: "LOADED_CELLS",
  });

  const loadSteps = (parent, dateString) => loadProjectionBreakdown({
    level: "steps", parent, dateString, responseField: "steps", loadedType: "LOADED_STEPS",
  });

  const loadSubsteps = (parent, dateString) => loadProjectionBreakdown({
    level: "substeps", parent, dateString, responseField: "substeps", loadedType: "LOADED_SUBSTEPS",
  });

  const loadServices = (parent, dateString) => loadProjectionBreakdown({
    level: "services", parent, dateString, responseField: "services", loadedType: "LOADED_SERVICES",
  });

  const selectDetail = (item, type) => dispatch({
    type: "SELECTED_DETAIL",
    data: { item, type }
  })

  // `source: 'registry'` troca a fonte das obras de `wbs_buildings` (medição) para
  // `buildings` (cadastro). Só a tela de Construtoras e Obras deve usar isso: o `_id` do
  // cadastro NÃO serve para `users.buildingIds`, que o dashboard resolve em `wbs_buildings`.
  const listCompanies = async (companyId, { includeInactive, source } = {}) => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();

    setIsLoading(true)

    const params = new URLSearchParams()
    if (companyId !== undefined) params.set("companyId", companyId)
    if (includeInactive) params.set("includeInactive", "1")
    if (source) params.set("source", source)
    const query = params.toString()
    const url = `${process.env.SERVER_URL}/project/listCompanies${query ? `?${query}` : ""}`;
    const httpResponse = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    const body = await httpResponse.json()
    // Erros de handler (5xx) voltam como uma STRING JSON (`error.message`), não um objeto —
    // sem o `ok`/`code` aqui, uma falha viraria silenciosamente `{companies: undefined}` na
    // tela, sem nenhum aviso de erro.
    const response = (typeof body === "object" && body !== null) ? body : { message: body }

    setIsLoading(false)
    return { ok: httpResponse.ok, code: httpResponse.status, ...response }
  }

  const createCompany = async data => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/createCompany`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data)
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  const createBuilding = async data => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/createBuilding`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data)
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  const updateCompany = async data => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateCompany`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data)
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  const getCompany = async companyId => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/getCompany?companyId=${companyId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      // Sem isto, uma resposta que não parseia como JSON (erro de gateway, timeout) deixava
      // setIsLoading(false) sem executar: o spinner global ficava girando pra sempre e o
      // modal de edição nunca abria, sem nenhum feedback de erro na tela.
      return { ok: false, message: error?.message || 'Não foi possível carregar a construtora.' }
    } finally {
      setIsLoading(false)
    }
  }

  const getBuilding = async buildingId => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/getBuilding?buildingId=${buildingId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      // Mesmo racional de getCompany: sem o finally, uma falha aqui travava o spinner pra
      // sempre em vez de mostrar "Não foi possível carregar a obra." — o sintoma relatado.
      return { ok: false, message: error?.message || 'Não foi possível carregar a obra.' }
    } finally {
      setIsLoading(false)
    }
  }

  const deleteCompany = async companyId => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/deleteCompany`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ company_id: companyId }),
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  const deleteBuilding = async buildingId => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/deleteBuilding`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ building_id: buildingId }),
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  const updateBuilding = async data => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateBuilding`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data)
    })
    const body = await response.json()
    setIsLoading(false)
    return { ok: response.ok, code: response.status, ...body }
  }

  // Diagnóstico da obra recém-cadastrada: quais campos o data-pipeline exige e ainda faltam,
  // se já houve ingestão (`ingested`) e qual foi a última execução. Recebe o `_id` do
  // CADASTRO (`buildings`), não o de medição.
  const getBuildingBootstrap = async buildingId => {
    const { token } = getProjectIdentity();
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/bootstrapBuilding?building_id=${buildingId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      return { ok: false, message: error?.message || 'Não foi possível verificar o status da obra.' }
    }
  }

  // Andamento da PRIMEIRA execução. Reusa o GET de bootstrapBuilding porque ele acha o job por
  // `update_jobs.building_id` (id do cadastro) e não depende de `wbs_buildings`, que ainda não
  // existe — diferente de `getUpdateJob`, que resolveria 404 nesse momento.
  const getBootstrapJob = async buildingId => {
    const res = await getBuildingBootstrap(buildingId)
    return { ok: !!res?.ok, job: res?.job ?? null, message: res?.message }
  }

  // Dispara a PRIMEIRA execução do pipeline. É o que cria `wbs_buildings` — sem isso a obra
  // fica cadastrada e invisível para todos os usuários. `userIds` é a liberação de acesso
  // escolhida ANTES de executar: mesma decisão operacional, e fica pendente até a ingestão.
  const bootstrapBuilding = async (buildingId, userIds = []) => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/bootstrapBuilding`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ building_id: buildingId, user_ids: userIds }),
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      return { ok: false, message: error?.message || 'Não foi possível iniciar a primeira execução.' }
    } finally {
      setIsLoading(false)
    }
  }

  const getBuildingAccess = async buildingId => {
    const { token } = getProjectIdentity();
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/buildingAccess?building_id=${buildingId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      return { ok: false, message: error?.message || 'Não foi possível carregar os acessos da obra.' }
    }
  }

  // `userIds` é a lista COMPLETA de quem deve ter acesso: quem for candidato e não estiver na
  // lista tem o acesso revogado. É por isso que a tela mostra uma confirmação antes.
  const updateBuildingAccess = async (buildingId, userIds) => {
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    setIsLoading(true)
    try {
      const response = await fetch(`${process.env.SERVER_URL}/project/buildingAccess`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ building_id: buildingId, user_ids: userIds }),
      })
      const body = await response.json()
      return { ok: response.ok, code: response.status, ...body }
    } catch (error) {
      return { ok: false, message: error?.message || 'Não foi possível atualizar os acessos.' }
    } finally {
      setIsLoading(false)
    }
  }

  const getResources = async (id, page, order, pageSize, searchString) => {

    // `project_id` deixa o back-end ler o snapshot ativo da obra (obras novas, como a Cota 365,
    // não têm nada nas coleções ao vivo).
    const projectId = encodeURIComponent(state.activeProject?._id ?? '')
    const response = await fetch(`${process.env.SERVER_URL}/project/getResources?project_id=${projectId}&id=${id}&page=${page}&order=${order}&pageSize=${Number(pageSize)}&searchString=${searchString}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  // `buildingId` acompanha o `id` da EAP porque contrato de obra MEGA nao tem
  // apropriacao: sem ele o recorte so por EAP nunca alcanca esses contratos e a
  // tela fica vazia com o dado gravado no banco.
  // `options` (opcional, último argumento) leva os parâmetros da visão por contrato das obras
  // MEGA: `view: "contract"` agrupa as linhas de item por `contract_number` no back-end, e
  // `balance: "open" | "all"` recorta pelo saldo a faturar. Sem `options` a URL é a de sempre,
  // então o detalhe do item e a aba de Contratos do Sienge não mudam.
  const getContracts = async (type, id, grouped = 'true', dateLimit, skip, docFilter, search, levelToGet, itemLevel, buildingId = '', options = {}) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)

    const contractViewQuery = [
      options?.view ? `&view=${encodeURIComponent(options.view)}` : '',
      options?.balance ? `&balance=${encodeURIComponent(options.balance)}` : '',
    ].join('')
    const searchValue = search?.search === undefined ? search?.search : encodeURIComponent(search.search)
    const response = await fetch(`${process.env.SERVER_URL}/project/getContracts?type=${type}&id=${id}&grouped=${grouped}&beginDate=${dateLimit?.min}&endDate=${dateLimit?.max}&skip=${skip}&docs=${docFilter}&search=${searchValue}&searchFor=${search?.searchFor}&levelFilter=${levelToGet}&levelWbs=${itemLevel}&building_id=${buildingId}${contractViewQuery}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)

    return response
  }

  const getCurve = async (buildingWbs, type, { page = 1, pageSize = 50, search = '' } = {}) => {
    const requestedWbs = Array.isArray(buildingWbs) ? buildingWbs.join(',') : buildingWbs
    const fallbackWbs = state?.activeProject?.wbs_code_ids?.join(',')
      || state?.activeProject?.wbs_code_id
    const ids = String(requestedWbs || fallbackWbs || '').trim()

    if (!ids) {
      return { items: [], page, pageSize, totalItems: 0, totalPages: 0, totalValue: null }
    }

    const query = new URLSearchParams({
      building_wbs: ids,
      type,
      page: String(page),
      pageSize: String(pageSize),
      search: String(search || ''),
      ...(state?.activeProject?._id
        ? { project_id: String(state.activeProject._id) }
        : {}),
    })
    const fetchResponse = await fetch(`${process.env.SERVER_URL}/project/getCurve?${query}`, {
      method: "GET"
    })
    const response = await fetchResponse.json()

    if (fetchResponse.ok === false) {
      throw new Error(response?.message || 'Não foi possível carregar a Curva ABC')
    }

    return normalizeCurveResponse(response, page, pageSize)
  }

  const getItemsCount = async (id, type) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const ids = state?.activeProject?.wbs_code_ids?.join(',')

    const response = await fetch(`${process.env.SERVER_URL}/project/getItemsQuantity?ids=${ids}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)

    return response
  }

  // `options.explicitMonth`: o chamador (painel mensal da lateral) fixa `dateString` como o
  // único mês da consulta. Nem o alargamento de `limitDate=false` nem o mês global da tabela
  // de projeção podem sobrescrevê-lo — o contexto item + mês + natureza é preservado.
  const getCreditors = async (type, id, page, dateString, limitDate = false, toPay, pageSize, searchString, documents, options = {}) => {

    let ids;
    if (typeof id === 'string') {
      ids = id
    } else if (!id) {
      ids = ''
    } else {
      ids = id.join(',')
    }
    let beginAt;
    let endAt;
    if (dateString) {
      const [month, year] = dateString.split("/")
      const date = new Date(year, month - 1);
      beginAt = date;
      endAt = date;
    } else {
      beginAt = state.filterDates.beginAt
      endAt = state.filterDates.endAt
    }
    const explicitMonth = Boolean(options?.explicitMonth && dateString);
    if (!limitDate && !explicitMonth) {
      beginAt = new Date(1900, 0, 1);
      endAt = new Date(2500, 11, 12);
    }
    if (!explicitMonth && state.projectionTable && window.location.pathname.includes("projecao-financeira")) {
      const [month, year] = state.tableMonthSelected.split("/")
      if (!month || !year) return
      const date = new Date(year, month - 1);
      beginAt = date;
      endAt = date;
    }
    const response = await fetch(`${process.env.SERVER_URL}/project/getCreditor?type=${type}&id=${ids}&page=${page}&toPay=${toPay}&begin_at=${beginAt.toISOString()}&end_at=${endAt.toISOString()}&pageSize=${pageSize}&searchString=${searchString}&documents=${documents.join()}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  const getAllCreditors = async (wbs, search = '') => {
    const response = await fetch(`${process.env.SERVER_URL}/project/getAllCreditors?wbs=${wbs}&search=${search}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  const getBills = async () => ({
    items: [],
    totalPages: 0,
    totalItemsCount: 0,
    code: 410,
    errorCode: state?.activeProject?._id
      ? "works_receivables_unavailable"
      : "project_scope_required",
  });

  const getResourceEAP = async (resource) => {
    const { setIsLoading } = useDashboardUIActions();
    const { id, project_id, building_wbs } = resource
    if (!project_id || !building_wbs || !Number.isSafeInteger(Number(id))) {
      return { ok: false, code: 400, errorCode: "project_scope_required" }
    }
    setIsLoading(true)
    const query = new URLSearchParams({
      project_id: String(project_id ?? ''),
      building_wbs: String(building_wbs ?? ''),
      id: String(id ?? ''),
    })
    const url = `/project/getResourcesEAP?${query.toString()}`

    const response = await fetch(`${process.env.SERVER_URL}${url}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    dispatch({
      type: "UPDATE_RESOURCE_EAP",
      data: response
    })

    setIsLoading(false)
    return response
  }

  const updateExtra = async (wbs_code_id, mutation, item_name) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const work_id = state.activeProject?._id;
    if (!work_id) return { code: 400, errorCode: "project_scope_required", message: "Informe a obra" };
    const { token } = getProjectIdentity();
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateExtraValue`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...mutation, item_name, work_id, wbs_code_id })
    })
    const payload = await response.json()
    setIsLoading(false)
    return { code: response.status, ...payload }
  }

  const updateStock = async (id, newValue, description, name, reference_date) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    const value = typeof newValue === 'string'
      ? +newValue.replace(',', '.')
      : Number(newValue || 0);
    if (isNaN(value)) return
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateStock`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id,
        newStockValue: value,
        description,
        resource_name: name,
        ...(reference_date ? { reference_date } : {}),
      })
    })
    const { message } = await response.json()
    setIsLoading(false)
    return { code: response.status, message }
  }


  const getStock = async (wbs_code_id, type, name) => {
    const ids = state?.activeProject?.wbs_code_ids?.join(',')
    const response = await fetch(`${process.env.SERVER_URL}/project/getStock?wbs_code_id=${wbs_code_id}&type=${type}&searchString=${name}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  const getTrendSettings = async (company_id) => {
    const { token } = getProjectIdentity();
    const response = await fetch(
      `${process.env.SERVER_URL}/project/getTrendSettings?company_id=${company_id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return { ok: response.ok, ...(await response.json()) };
  };

  const updateTrendSettings = async (payload) => {
    const { token } = getProjectIdentity();
    const response = await fetch(`${process.env.SERVER_URL}/project/updateTrendSettings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return { ok: response.ok, ...(await response.json()) };
  };

  const simulateTrend = async (payload) => {
    const { token } = getProjectIdentity();
    const response = await fetch(`${process.env.SERVER_URL}/project/simulateTrend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return { ok: response.ok, ...(await response.json()) };
  };


  const getAppropriation = async (type, wbs = '') => {
    const ids = state?.activeProject?.wbs_code_ids?.join(',')
    const response = await fetch(`${process.env.SERVER_URL}/project/getAppropriationItems?type=${type}&id=${ids}&wbs=${wbs}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    // const response = await fetch(`${process.env.SERVER_URL}/project/getAppropriationItems?type=${type}&id=${ids}&wbs=AMPLI01000101001003001`, {
    //   method: "GET"
    // }).then(res => {
    //   return res.json()
    // })

    return response
  }

  const updateAdvancement = async (mutation) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const work_id = state.activeProject?._id;
    if (!work_id) return { code: 400, errorCode: "project_scope_required", message: "Informe a obra" };
    const { setIsLoading } = useDashboardUIActions();
    const { token } = getProjectIdentity();
    const request = { ...mutation };
    if (request.operation === "create") {
      request.value = typeof request.value === 'string'
        ? +request.value.replace(',', '.')
        : Number(request.value || 0);
      if (isNaN(request.value)) return
    }
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateAdvancement`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...request, work_id })
    })
    const payload = await response.json()
    setIsLoading(false)
    return { code: response.status, ...payload }
  }

  const getAdvancements = async (building_id, wbs = '') => {
    const response = await fetch(`${process.env.SERVER_URL}/project/getAdvancements?wbs=${wbs}&building_id=${building_id}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  const getExtraValues = async (building_id, wbs_code_id = '') => {
    const response = await fetch(`${process.env.SERVER_URL}/project/getExtraValues?wbs_code_id=${wbs_code_id}&building_id=${building_id}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    return response
  }

  const getWbsPath = async (wbs, building_wbs) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/getWbsPath?building_wbs=${building_wbs}&wbs=${wbs}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)
    return response
  }

  const selectXlsxData = async (data) => {
    dispatch({
      type: "SELECT_XLSX_DATA",
      data: stripWorksExport(data),
    });
  };

  const selectGraph = async (data) => {
    dispatch({
      type: "SELECT_GRAPH",
      data,
    });
  };

  const setDocsToFilter = async (data) => {
    dispatch({
      type: "FILTER_DOCS",
      data,
    });
  };

  const getReport = async (collection) => {
    if (!WORKS_REPORT_COLLECTIONS.includes(collection))
      return {
        code: 410,
        errorCode: "works_receivables_unavailable",
        items: [],
      };
    const project = state.activeProject;
    const wbs = project?.wbs_code_ids?.[0] ?? project?.wbs_code_id;
    const { beginAt, endAt } = state.filterDates ?? {};
    if (
      !project?._id ||
      !project.building_id ||
      !wbs ||
      !(beginAt instanceof Date) ||
      !(endAt instanceof Date) ||
      !Number.isFinite(beginAt.getTime()) ||
      !Number.isFinite(endAt.getTime())
    )
      return { code: 400, errorCode: "project_scope_required", items: [] };
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        project_id: project._id,
        wbs_id: wbs,
        building_id: project.building_id,
        collectionToGet: collection,
        begin_at: beginAt.toISOString(),
        end_at: endAt.toISOString(),
      });
      const response = await fetch(
        `${process.env.SERVER_URL}/project/getReport?${query.toString()}`,
        { method: "GET" },
      );
      const payload = await response.json();
      return { ...payload, code: response.status };
    } catch {
      return { code: 0, errorCode: "network_error", items: [] };
    } finally {
      setIsLoading(false);
    }
  };

  const getAllBuildingItems = async (types) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const { activeProject: { wbs_code_ids } } = state
    const response = await fetch(`${process.env.SERVER_URL}/project/getAllBuildingItems?id=${wbs_code_ids?.join(',')}&types=${types.join(',')}`, {
      method: "GET",
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)
    return response
  }

  const getAdvancedItems = async (id, level) => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/getAdvancedItems?building_id=${id}&level=${level}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    dispatch({
      type: "UPDATE_ADVANCED_ITEMS",
      data: response.items,
    });
    setIsLoading(false)
    return response
  }

  const getTimeline = async (wbs, id, paid, dateLimit, skip, docFilter, search, levelToGet = '', itemLevel = '') => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    let ids;
    if (wbs === undefined) {
      ids = state?.activeProject?.wbs_code_ids?.join(',')
    } else {
      ids = wbs?.join(',')
    }
    const response = await fetch(`${process.env.SERVER_URL}/project/getTimeline?wbs_code_id=${ids}&id=${id}&paid=${paid === "paid"}&beginDate=${dateLimit.min}&endDate=${dateLimit.max}&skip=${skip}&docs=${docFilter}&search=${search.search}&searchFor=${search.searchFor}&levelFilter=${levelToGet}&levelWbs=${itemLevel}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)
    return response
  }

  const getAllCartesianForm = async () => {
    const response = await fetch(`https://6uc1i3mmsh.execute-api.us-east-1.amazonaws.com/prod/cartesian/getForm?all=${true}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })

    return response
  }

  const getLessons = async (wbs_code_id = '') => {
    const { building_id } = state.activeProject
    const response = await fetch(`${process.env.SERVER_URL}/project/getLessons?wbs_code_id=${wbs_code_id}&building_id=${building_id}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    return response
  }

  const updateLessons = async (wbs_code_id, lesson, id = '', itemName, dlt = "false", tags, createdBy) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const { building_id } = state.activeProject
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateLessons`, {
      method: "POST",
      body: JSON.stringify({ wbs_code_id, lesson, id, building_id, dlt, itemName, tags, createdBy })
    })
    const { newId, message } = await response.json();
    setIsLoading(false)
    return { code: response.status, message, newId }
  }

  const updateCompanyTags = async (company_id, tags) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateCompanyTags`, {
      method: "POST",
      body: JSON.stringify({ company_id, tags })
    })
    setIsLoading(false)
    return { code: response.status, tags }
  }

  const updateBuildingInfo = async (building_id, company_id, custom_name) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const response = await fetch(`${process.env.SERVER_URL}/project/updateBuildingInfo`, {
      method: "POST",
      body: JSON.stringify({ building_id, company_id, custom_name })
    })
    setIsLoading(false)
    return { code: response.status }
  }

  // `searchFor` (opcional): `supplier_name` busca pelo nome do fornecedor; vazio ou
  // `contract_number` mantém a busca pelo número do pedido, como antes.
  const getPurchaseOrders = async (dateLimit, skip, docFilter, search, orderId, levelToGet, itemLevel, searchFor = '') => {
    const { setIsLoading } = useDashboardUIActions();
    setIsLoading(true)
    const searchValue = search === undefined || search === null ? search : encodeURIComponent(search)
    const searchForQuery = searchFor ? `&searchFor=${encodeURIComponent(searchFor)}` : ''
    const response = await fetch(`${process.env.SERVER_URL}/project/getPurchaseOrders?building_id=${state.activeProject?.building_id}&beginDate=${dateLimit.min}&endDate=${dateLimit.max}&skip=${skip}&docs=${docFilter}&search=${searchValue}${searchForQuery}&orderId=${orderId}&levelFilter=${levelToGet}&levelWbs=${itemLevel}`, {
      method: "GET"
    }).then(res => {
      return res.json()
    })
    setIsLoading(false)
    return response
  }

  const createUpdateJob = async (type, period) => {
    const rejected = presentationMutationRejection();
    if (rejected) return rejected;
    const result = await requestUpdateJob({
      fetchFn: fetch,
      serverUrl: process.env.SERVER_URL,
      companyId: state.activeProject?.company_id,
      buildingId: state.activeProject?.building_id,
      type,
      period,
    });
    if (result.body?.job && state.activeProject?.building_id) {
      dispatch({
        type: "UPDATE_JOB_STATUS",
        data: {
          buildingId: state.activeProject.building_id,
          job: result.body.job,
        },
      });
    }
    return result;
  };

  const getUpdateJob = async (buildingId = state.activeProject?.building_id) => {
    if (!buildingId) return { ok: false, status: 400, body: null };
    const result = await loadUpdateJob({
      fetchFn: fetch,
      serverUrl: process.env.SERVER_URL,
      buildingId,
    });
    if (result.ok) {
      dispatch({
        type: "UPDATE_JOB_STATUS",
        data: { buildingId, job: result.body?.job ?? null },
      });
    }
    return result;
  };

  const refreshPurchaseOrders = async (startDate, endDate) => {
    // `startDate`/`endDate` nulos pedem o histórico completo (só MEGA) -- `createUpdateJob`
    // precisa receber `null` explícito, não um objeto com os dois campos vazios.
    if (startDate === null && endDate === null) {
      return createUpdateJob("purchase_orders", null);
    }
    return createUpdateJob("purchase_orders", {
      start_date: startDate,
      end_date: endDate,
    });
  };

  const supplyFetch = async (url, options = {}) => {
    const { token } = getProjectIdentity();
    try {
      const res = await fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const getSupplySchedule = (building_wbs, building_id) =>
    supplyFetch(`${process.env.SERVER_URL}/project/getSupplySchedule?building_wbs=${building_wbs}&building_id=${building_id}`);

  const setSupplyStageDate = (building_id, resource_id, target_stage_key, effective_date = null, source = null) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/setSupplyStageDate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ building_id, resource_id, target_stage_key, effective_date, source }),
    });
  };

  const reprogramSupplyNeedDate = (building_id, resource_id, need_date, source = "manual", need_date_suggested = null) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/reprogramSupplyNeedDate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ building_id, resource_id, need_date, source, need_date_suggested }),
    });
  };

  const toggleSupplyStageLock = (building_id, resource_id, stage_key) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/toggleSupplyStageLock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ building_id, resource_id, stage_key }),
    });
  };

  const ignoreSupplySuggestion = (building_id, resource_id, need_date_suggested) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/ignoreSupplySuggestion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ building_id, resource_id, need_date_suggested }),
    });
  };

  const getSupplyItemDetail = (building_id, resource_id) =>
    supplyFetch(`${process.env.SERVER_URL}/project/getSupplyItemDetail?building_id=${building_id}&resource_id=${resource_id}`);

  const getProcurementTemplate = (company_id, all = false) =>
    supplyFetch(`${process.env.SERVER_URL}/project/getProcurementTemplate?company_id=${company_id}${all ? "&all=1" : ""}`);

  // payload: { action?: "create"|"update"|"rename"|"archive", template_id?, name?, stages? }
  // Sem action/template_id o BE atualiza o ciclo padrão (comportamento legado).
  const updateProcurementTemplate = (company_id, payload) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/updateProcurementTemplate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ company_id, ...payload }),
    });
  };

  const setSupplyItemTemplate = (building_id, resource_id, template_id) => {
    const rejected = presentationMutationRejection();
    if (rejected) return Promise.resolve(rejected);
    return supplyFetch(`${process.env.SERVER_URL}/project/setSupplyItemTemplate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ building_id, resource_id, template_id }),
    });
  };

  // Compatibilidade para consumidores antigos: o Prevision agora usa o mesmo job durável.
  const refreshPrevision = async (building_id, company_id) => {
    if (
      building_id !== state.activeProject?.building_id
      || company_id !== state.activeProject?.company_id
    ) return { ok: false, status: 400, body: null };
    return createUpdateJob("prevision");
  };

  return {
    list,
    selectProject,
    changeDates,
    loadUnits,
    loadCells,
    loadSteps,
    loadSubsteps,
    loadServices,
    selectDetail,
    getFollowUps,
    createUpdateJob,
    getUpdateJob,
    listCompanies,
    createCompany,
    createBuilding,
    updateCompany,
    updateBuilding,
    deleteCompany,
    deleteBuilding,
    getCompany,
    getBuilding,
    getBuildingBootstrap,
    getBootstrapJob,
    bootstrapBuilding,
    getBuildingAccess,
    updateBuildingAccess,
    getResources,
    getCreditors,
    getAllCreditors,
    getCurve,
    updateCurveID,
    getResourceEAP,
    getContracts,
    updateExtra,
    updateStock,
    getAppropriation,
    updateAdvancement,
    getAdvancements,
    getWbsPath,
    updateCubBased,
    updateForecastBased,
    updateProjectLive,
    selectXlsxData,
    getItemsCount,
    getReport,
    getAllBuildingItems,
    selectGraph,
    setDocsToFilter,
    getBills,
    changeGraphDates,
    getSingleFollowUps,
    getScheduleAnalysis,
    updateMaxDates,
    getAdvancedItems,
    getExtraValues,
    getTimeline,
    getAllCartesianForm,
    getStock,
    getTrendSettings,
    updateTrendSettings,
    simulateTrend,
    updateProjectionTableView,
    updateProjectionTableDate,
    getLessons,
    updateLessons,
    getPurchaseOrders,
    refreshPurchaseOrders,
    updateCompanyTags,
    updateBuildingInfo,
    getSupplySchedule,
    setSupplyStageDate,
    reprogramSupplyNeedDate,
    toggleSupplyStageLock,
    ignoreSupplySuggestion,
    getSupplyItemDetail,
    getProcurementTemplate,
    updateProcurementTemplate,
    setSupplyItemTemplate,
    refreshPrevision,
  };
};
